package com.neurobs.ccabserver.administrator.controller

import com.neurobs.ccab.{CreateUserData, IdentityGroup, ResetUserPasswordData, UpdateUserData, UserDataRest, CCABServer => CCABServerNew}
import com.neurobs.ccab.db.UserData
import com.neurobs.ccabserver.administrator.view.UserListView
import com.neurobs.js.ccab.CCABServer
import com.neurobs.js.controller.{AdminListAdapter, AdminListController, AdminPanelDelegate}
import com.neurobs.js.dom.RecordTableView.{CommandLinkCell, GeneralCell}
import com.neurobs.js.dom.ElementFactory._
import com.neurobs.js.dom.DOMViewElement._
import com.neurobs.js.dom.InputCheckBox
import com.neurobs.js.util.StringUtilities.dateString
import com.neurobs.js.view.{Form, Gui}
import org.scalajs.dom.raw.HTMLElement
import scalatags.JsDom.all._

import scala.concurrent.{Future, Promise}
import scala.concurrent.ExecutionContext.Implicits.global

class UserListController (accountId: Long, primaryEmail: String, currentUserId: Long, server: CCABServerNew, gui: Gui, legacy: Boolean = true) extends AdminListAdapter[UserDataRest] {

	override def title: String = if (legacy) s"Account $accountId ($primaryEmail) Users" else "Users"

	override def itemName = "User"

	override def columnHeaders = Seq(
//		"Id",
		"Created",
		"Email",
		"Name",
		"Roles",
		"Status",
		""
	)

	override def itemRow(u: UserDataRest, delegate: Option[AdminPanelDelegate], controller: AdminListController[UserDataRest]) = {
		//println(s"UserListController.itemRow groups: ${u.groups}")
		val roles = u.groups.map(IdentityGroup.displayName).sorted.mkString(", ")
		val actions =
			if (!u.migrated) CommandLinkCell(text = "Migrate", () => createOrMigrateUser(Some(u.id)))
			else {
				val resetLink = a(href := "#", `class` := "efactory-link")("Reset password").render
				val editLink = a(href := "#", `class` := "efactory-link")("Edit").render
				resetLink.onclick = (_: org.scalajs.dom.raw.Event) => {
					resetPassword(u)
					false
				}
				editLink.onclick = (_: org.scalajs.dom.raw.Event) => {
					editUser(u)
					false
				}
				GeneralCell(span(resetLink, raw("&nbsp;|&nbsp;"), editLink).render)
			}
		Seq(
//			u.id.toString,
			u.createdAt,
			u.email,
			s"${u.firstName} ${u.lastName}",
			roles,
			if (!u.migrated) UserListController.migrationLabel() else if (u.disabled) UserListController.disabledLabel() else UserListController.activeLabel(),
			actions
		)
	}

	override val create = Some(createUser)

	override def get(): Future[Either[String, Seq[UserDataRest]]] = {
		server.getUsers(accountId)._2.map {
			case Left(error) => Left(error.description)
			case Right(data) => Right(data.sortBy(u => (!u.migrated, u.disabled, u.lastName, u.firstName)))
		}
	}

	private val showDisabledCheckbox: InputCheckBox = makeCheckbox(checked = false)
	private val showUnmigratedCheckbox: InputCheckBox = makeCheckbox(checked = false)

	private val filterControls: HTMLElement = {
		showDisabledCheckbox.setChangeHandler(() => refreshFilter())
		showUnmigratedCheckbox.setChangeHandler(() => refreshFilter())
		div(
			label(showDisabledCheckbox.input, " Display disabled users"),
			raw("&nbsp;&nbsp;&nbsp;&nbsp;"),
			label(showUnmigratedCheckbox.input, " Display unmigrated users")
		).render
	}

	override def customContent: Option[HTMLElement] = Some(filterControls)

	override def filter(s: Seq[UserDataRest]): Seq[UserDataRest] =
		s.filter(u => (showDisabledCheckbox.checked || !u.disabled) && (showUnmigratedCheckbox.checked || u.migrated))

	private def resetPassword(user: UserDataRest): Unit =  {
		val form = UserListView.resetPasswordForm(user.email)

		def process(callback: (Option[String]) => Unit): Either[String, Form.Process] = {
			val data = ResetUserPasswordData(
				password = form.password.value()
			)
			if (data.password.isEmpty || form.passwordConfirm.value().isEmpty)
				Left("All fields are required.")
			else if (data.password != form.passwordConfirm.value())
				Left("The passwords don't match.")
			else
				Right(
					CCABServer.resetUserPassword(user.id, data) {
						case Left(error) => callback(Some(error.message))
						case Right(res) => res.error match {
							case Some(err) => callback(Some(err))
							case None => callback(None)
						}
					}
				)
		}

		gui.showModalForm(form.form, process, processCancellable =  true, Some(() => Unit))
	}

	private def createUser(c: AdminListController[UserDataRest]): Unit = {
		createOrMigrateUser(migrateUserId = None)
	}

	private def editUser(user: UserDataRest): Unit = {
		val form = UserListView.editForm()
		val currentlyAccountAdmin = user.groups.contains(IdentityGroup.AccountAdmin)
		val editingSelf = user.id == currentUserId

		form.email.setValue(user.email)
		form.firstName.setValue(user.firstName)
		form.lastName.setValue(user.lastName)
		form.accountAdmin.setChecked(currentlyAccountAdmin)
		form.disabled.setChecked(user.disabled)

		if (editingSelf) {
			form.accountAdmin.setReadOnly(true)
			form.disabled.setReadOnly(true)
		}

		def process(callback: Option[String] => Unit): Either[String, Form.Process] = {
			val data = UpdateUserData(
				firstName = form.firstName.value(),
				lastName = form.lastName.value(),
				accountAdmin = form.accountAdmin.checked,
				disabled = form.disabled.checked
			)

			if (data.firstName.isEmpty || data.lastName.isEmpty)
				Left("All fields are required.")
			else if (editingSelf && ((data.accountAdmin != currentlyAccountAdmin) || (data.disabled != user.disabled)))
				Left("You cannot change your own Account Admin or Disabled settings.")
			else
				Right(CCABServerNew.handleResponse(server.updateUser(accountId = accountId, userId = user.id, data = data), callback) { _ => refresh() })
		}

		gui.showModalForm(form.form, process, processCancellable = true, Some(() => Unit))
	}

	private def createOrMigrateUser(migrateUserId: Option[Long]): Unit = {
		val form = UserListView.createForm(migrateUserId.isDefined)

		def process(callback: (Option[String]) => Unit): Either[String, Form.Process] = {
			val data = CreateUserData(
				email = form.email.value(),
				firstName = form.firstName.value(),
				lastName = form.lastName.value()
			)
			if (data.email.isEmpty || data.firstName.isEmpty || form.lastName.value().isEmpty)
				Left("All fields are required.")
			else {
				migrateUserId match {
					case Some(userId) => Right(CCABServerNew.handleResponse(server.migrateUser(accountId, userId, data), callback) { _ => refresh() })
					case None => Right(CCABServerNew.handleResponse(server.createUser(accountId, data), callback) { _ => refresh() })
				}
			}
		}

			gui.showModalForm(form.form, process, true, Some(() => Unit))
	}
}

object UserListController {
	private def migrationLabel() = span("Requires Migration", style := "color: orange;").render
	private def disabledLabel() = span("Disabled", style := "color: red;").render
	private def activeLabel() = span("Active", style := "color: green;").render

	def apply(accountId: Long, primaryEmail: String, currentUserId: Long, server: CCABServerNew, gui: Gui, legacy: Boolean = true): AdminListController[UserDataRest] = new AdminListController[UserDataRest](new UserListController(accountId, primaryEmail, currentUserId, server, gui, legacy = legacy))
}